/**
 * @fileoverview Minimal topic-hub membership maps for related-resource scoring.
 * @description Generated from the resource topic registry so client-side fallback
 * scorers do not import the full display-copy registry bundle.
 * @module resources/relevance/topic-hub-affinity-map
 */

import type { ResourceTopicId } from "~/lib/resources/topics/types"

export const TOOL_TOPIC_IDS: Readonly<
  Record<string, readonly ResourceTopicId[]>
> = {
  "hook-rate-benchmark": [
    "creative-analytics",
    "meta-advertising",
    "tiktok-advertising",
    "video-performance",
    "marketing-benchmarks",
  ],
  "paid-social-benchmarks": [
    "marketing-benchmarks",
    "meta-advertising",
    "tiktok-advertising",
  ],
  "thruplay-rate-benchmark": [
    "creative-analytics",
    "creative-strategy",
    "meta-advertising",
    "tiktok-advertising",
    "video-performance",
    "marketing-benchmarks",
  ],
  "creative-quality-grader": [
    "creative-analytics",
    "creative-testing",
    "creative-strategy",
    "marketing-benchmarks",
  ],
  "video-dropoff-calculator": [
    "creative-analytics",
    "creative-strategy",
    "tiktok-advertising",
    "video-performance",
  ],
  "cpv-calculator": ["creative-analytics"],
  "creative-testing-calculator": ["creative-testing", "experimentation"],
  "ab-test-statistical-significance-calculator": [
    "creative-testing",
    "experimentation",
  ],
  "ad-mockup-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "notification-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "app-store-screenshot-generator": [
    "creative-strategy",
    "social-proof-creative",
  ],
  "screenshot-beautifier": ["creative-strategy"],
  "buyer-persona-interactive-generator": ["creative-strategy"],
  "color-palette-generator": ["creative-strategy"],
  "fake-chat-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "ai-chat-mockup-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "email-thread-mockup-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "review-testimonial-generator": [
    "creative-strategy",
    "meta-advertising",
    "social-proof-creative",
  ],
  "cpm-calculator": ["meta-advertising", "marketing-benchmarks"],
  "cpa-calculator": ["attribution-measurement", "marketing-benchmarks", "meta-advertising"],
  "roas-calculator": [
    "meta-advertising",
    "attribution-measurement",
    "marketing-benchmarks",
  ],
  "instagram-post-mockup-generator": ["meta-advertising"],
  "facebook-post-mockup-generator": ["meta-advertising"],
  "tiktok-post-mockup-generator": ["tiktok-advertising"],
  "mer-calculator": ["attribution-measurement"],
  "marketing-incrementality-calculator": [
    "attribution-measurement",
    "experimentation",
  ],
  "customer-ltv-calculator": ["attribution-measurement"],
  "marketing-budget-calculator": ["attribution-measurement"],
  "ugc-cost-calculator": ["creative-testing", "experimentation"],
  "chart-graph-mockup-generator": ["data-visualization"],
  "data-table-generator": ["data-visualization"],
  "social-media-post-mockup-generator": ["social-proof-creative"],
  "comment-thread-mockup-generator": ["social-proof-creative"],
  "browser-mockup-generator": ["social-proof-creative"],
} as const

export const GLOSSARY_TOPIC_IDS: Readonly<
  Record<string, readonly ResourceTopicId[]>
> = {
  "metrics:thumbstop-rate-tsr": [
    "creative-analytics",
    "video-performance",
    "marketing-benchmarks",
  ],
  "metrics:hold-rate": [
    "creative-analytics",
    "video-performance",
    "marketing-benchmarks",
  ],
  "metrics:thruplay-rate": [
    "creative-analytics",
    "video-performance",
    "marketing-benchmarks",
  ],
  "metrics:video-completion-rate-vcr": [
    "creative-analytics",
    "video-performance",
  ],
  "metrics:thumbstop-click-rate": ["creative-analytics"],
  "metrics:cost-per-thruplay": ["creative-analytics", "video-performance"],
  "creative:creative-testing": ["creative-testing"],
  "creative:ab-testing": ["creative-testing", "experimentation"],
  "metrics:statistical-significance": ["creative-testing", "experimentation"],
  "metrics:sample-size": ["creative-testing", "experimentation"],
  "metrics:confidence-interval": ["creative-testing", "experimentation"],
  "creative:ad-variations": ["creative-testing", "creative-strategy"],
  "creative:creative-fatigue": ["creative-strategy", "meta-advertising"],
  "platform:meta-reels-ads": ["meta-advertising"],
  "metrics:click-through-rate-ctr": [
    "meta-advertising",
    "marketing-benchmarks",
  ],
  "metrics:cost-per-mille-cpm": ["meta-advertising", "marketing-benchmarks"],
  "metrics:cost-per-click-cpc": ["marketing-benchmarks"],
  "platform:tiktok-ads": ["tiktok-advertising"],
  "platform:tiktok-spark-ads": ["tiktok-advertising"],
  "metrics:impressions": ["video-performance"],
  "metrics:view-rate": ["video-performance"],
  "metrics:return-on-ad-spend-roas": [
    "attribution-measurement",
    "marketing-benchmarks",
  ],
  "metrics:marketing-efficiency-ratio-mer": ["attribution-measurement"],
  "metrics:attributed-marketing-efficiency-ratio-amer": [
    "attribution-measurement",
  ],
  "metrics:new-marketing-efficiency-ratio-nmer": ["attribution-measurement"],
  "metrics:customer-acquisition-cost-cac": ["attribution-measurement"],
  "general:incrementality": ["attribution-measurement", "experimentation"],
  "general:attribution-window": ["attribution-measurement"],
  "charts:horizontal-bar-chart": ["data-visualization"],
  "charts:vertical-bar-chart": ["data-visualization"],
  "charts:grouped-bar-chart": ["data-visualization"],
  "charts:stacked-bar-chart": ["data-visualization"],
  "charts:line-chart": ["data-visualization"],
  "charts:area-chart": ["data-visualization"],
  "charts:funnel-chart": ["data-visualization"],
  "charts:pie-chart": ["data-visualization"],
  "charts:scatter-plot": ["data-visualization"],
  "charts:histogram": ["data-visualization"],
  "charts:heat-map": ["data-visualization"],
  "charts:stacked-area-chart": ["data-visualization"],
  "charts:sparkline": ["data-visualization"],
  "charts:box-plot": ["data-visualization"],
  "charts:bubble-chart": ["data-visualization"],
  "charts:chord-diagram": ["data-visualization"],
  "charts:density-plot": ["data-visualization"],
  "charts:donut-chart": ["data-visualization"],
  "charts:network-graph": ["data-visualization"],
  "charts:radar-chart": ["data-visualization"],
  "charts:sankey-diagram": ["data-visualization"],
  "charts:stream-graph": ["data-visualization"],
  "charts:treemap": ["data-visualization"],
  "charts:word-cloud": ["data-visualization"],
  "metrics:margin-of-error": ["experimentation"],
  "metrics:control-group": ["experimentation"],
  "metrics:false-positive": ["experimentation"],
  "metrics:false-negative": ["experimentation"],
  "creative:social-proof": ["social-proof-creative"],
  "creative:testimonial": ["social-proof-creative"],
  "creative:social-proof-ads": ["social-proof-creative"],
} as const
